import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverOptions {
	threshold?: number;
	rootMargin?: string;
	triggerOnce?: boolean;
}

export function useIntersectionObserver<T extends HTMLElement = HTMLDivElement>(
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	_options: UseIntersectionObserverOptions = {}
) {
	const [isIntersecting, setIsIntersecting] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const elementRef = useRef<T>(null);

	useEffect(() => {
		const element = elementRef.current;
		if (!element) {
			return;
		}

		const checkVisibility = () => {
			const rect = element.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const viewportWidth = window.innerWidth;

			// Check if element is fully in view
			const isFullyInView = rect.top >= 0 &&
				rect.bottom <= viewportHeight &&
				rect.left >= 0 &&
				rect.right <= viewportWidth;

			setIsIntersecting(isFullyInView);

			if (isFullyInView) {
				// Find all sibling elements that are also fully in view
				const parent = element.parentElement;
				if (!parent) {
					return;
				}

				const siblings = Array.from(parent.children) as HTMLElement[];
				const fullyVisibleSiblings = siblings.filter(sibling => {
					const siblingRect = sibling.getBoundingClientRect();
					return siblingRect.top >= 0 &&
						siblingRect.bottom <= viewportHeight &&
						siblingRect.left >= 0 &&
						siblingRect.right <= viewportWidth;
				});

				// If only one tile is fully in view, it should play
				if (fullyVisibleSiblings.length === 1) {
					setIsPlaying(true);
				}
				// If two tiles are fully in view, the higher one should play
				else if (fullyVisibleSiblings.length === 2) {
					const sortedSiblings = fullyVisibleSiblings.sort((a, b) => {
						const aRect = a.getBoundingClientRect();
						const bRect = b.getBoundingClientRect();
						return aRect.top - bRect.top;
					});

					// The higher one (first in sorted array) should play
					setIsPlaying(sortedSiblings[0] === element);
				}
				// If more than 2 tiles are visible, none should play
				else {
					setIsPlaying(false);
				}
			}
			else {
				setIsPlaying(false);
			}
		};

		// Initial check
		checkVisibility();

		// Listen for scroll events
		window.addEventListener('scroll', checkVisibility, { passive: true });
		window.addEventListener('resize', checkVisibility, { passive: true });

		return () => {
			window.removeEventListener('scroll', checkVisibility);
			window.removeEventListener('resize', checkVisibility);
		};
	}, []);

	return { elementRef, isIntersecting, isCentered: isPlaying };
}
